import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Users } from "lucide-react";

function Partners() {
  const partners = [
    { name: "TGIF", logo: "/images/tgif_logo.png" },
    { name: "TSF", logo: "/images/TSFlogo.png" },
    { name: "TEP-A", logo: "/images/tep-a.png" },
    { name: "Juiced Up", logo: "/images/Juiced up.png" },
    { name: "Libations with Finesse", logo: "/images/Libations with Finesse.png" },
    { name: "Partner", logo: "/images/Untitled_Artwork.png" },
    { name: "Partner Organization", logo: "/images/image0.jpeg" },
    { name: "Community Partner", logo: "/images/IMG_9275.PNG" }
  ];

  return (
    <div className="min-h-screen">
      {/* Header */}
      <section className="pt-32 pb-16 px-4 bg-gradient-to-br from-[#1167B1] to-[#0d5390] text-white">
        <div className="max-w-7xl mx-auto text-center">
          <Users className="w-16 h-16 mx-auto mb-6" />
          <Badge className="bg-[#FFE102] text-[#17181B] font-bold mb-6">Community Support</Badge>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Our Partners & Sponsors</h1>
          <p className="text-xl text-white/90 max-w-4xl mx-auto">
            Working together with community organizations and corporate sponsors to expand our impact and serve more youth across Metro Atlanta.
          </p>
        </div>
      </section>

      {/* Community Partners */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-[#1167B1] text-white mb-4">Community Partners</Badge>
            <h2 className="text-4xl font-bold text-[#17181B] mb-4">Local Organizations</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">These organizations work hand-in-hand with us to deliver programs and support to youth and families.</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-8 items-center">
            {partners.map((partner, index) => (
              <div key={index} className="flex items-center justify-center p-6 bg-gray-50 rounded-lg hover:shadow-xl transition-all">
                <img 
                  src={partner.logo} 
                  alt={partner.name} 
                  className="max-h-24 w-auto object-contain grayscale hover:grayscale-0 transition-all"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Corporate Sponsors */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-[#FFE102] text-[#17181B] font-bold mb-4">Corporate Sponsors</Badge>
            <h2 className="text-4xl font-bold text-[#17181B] mb-4">Business Support</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">These corporate partners provide essential resources, funding, and in-kind support to our programs.</p>
          </div>

          <div className="grid md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            <div className="flex items-center justify-center p-8 bg-gradient-to-br from-green-50 to-green-100 rounded-xl hover:shadow-xl transition-all">
              <div className="text-center">
                <div className="text-4xl font-bold text-green-700 mb-2">Publix</div>
                <div className="text-sm text-green-600">Supermarkets</div>
              </div>
            </div>
            <div className="flex items-center justify-center p-8 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl hover:shadow-xl transition-all">
              <div className="text-center">
                <div className="text-4xl font-bold text-blue-700 mb-2">Costco</div>
                <div className="text-sm text-blue-600">Wholesale</div>
              </div>
            </div>
            <div className="flex items-center justify-center p-8 bg-gradient-to-br from-red-50 to-red-100 rounded-xl hover:shadow-xl transition-all">
              <div className="text-center">
                <div className="text-4xl font-bold text-red-600 mb-2">Target</div>
                <div className="text-sm text-red-500">Retail</div>
              </div>
            </div>
            <div className="flex items-center justify-center p-8 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl hover:shadow-xl transition-all">
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-700 mb-2">4imprint</div>
                <div className="text-sm text-purple-600">Promotional Products</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partnership Benefits */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#17181B] mb-4">Why Partner With Us?</h2>
            <p className="text-xl text-gray-600">Together, we're creating lasting impact in Metro Atlanta communities.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="p-6 bg-gray-50 rounded-lg">
              <h3 className="text-2xl font-bold text-[#17181B] mb-4">Community Impact</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start space-x-2">
                  <div className="w-2 h-2 bg-[#1167B1] rounded-full mt-2"></div>
                  <span>Direct impact on underserved youth and families</span>
                </li>
                <li className="flex items-start space-x-2">
                  <div className="w-2 h-2 bg-[#1167B1] rounded-full mt-2"></div>
                  <span>Measurable outcomes and success stories</span>
                </li>
                <li className="flex items-start space-x-2">
                  <div className="w-2 h-2 bg-[#1167B1] rounded-full mt-2"></div>
                  <span>Breaking cycles of hardship through education</span>
                </li>
              </ul>
            </div>

            <div className="p-6 bg-gray-50 rounded-lg">
              <h3 className="text-2xl font-bold text-[#17181B] mb-4">Partnership Opportunities</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start space-x-2">
                  <div className="w-2 h-2 bg-[#FFE102] rounded-full mt-2"></div>
                  <span>Financial sponsorship</span>
                </li>
                <li className="flex items-start space-x-2">
                  <div className="w-2 h-2 bg-[#FFE102] rounded-full mt-2"></div>
                  <span>In-kind donations and resources</span>
                </li>
                <li className="flex items-start space-x-2">
                  <div className="w-2 h-2 bg-[#FFE102] rounded-full mt-2"></div>
                  <span>Volunteer and mentorship programs</span>
                </li>
                <li className="flex items-start space-x-2">
                  <div className="w-2 h-2 bg-[#FFE102] rounded-full mt-2"></div>
                  <span>Corporate social responsibility initiatives</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 bg-[#1167B1]">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-4xl font-bold mb-6">Interested in Partnering?</h2>
          <p className="text-xl mb-8 text-white/90">
            Join us in creating opportunities for Metro Atlanta youth. Let's build something meaningful together.
          </p>
          <Link to="/contact">
            <Button className="bg-[#FFE102] text-[#17181B] hover:bg-[#FFE102]/90 font-bold text-lg px-8 py-6 rounded-full">
              Get In Touch
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}

export default Partners;